import { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../context/AuthContext';

export const useMCPBooks = () => {
  const { user } = useAuth();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Call MCP server
  const callMCPServer = async (command, params = []) => {
    if (!user?.uid) {
      throw new Error('User must be authenticated to use MCP commands');
    }
    
    try {
      const response = await fetch(API_ENDPOINTS.MCP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          command: `${command} ${params.join(' ')}`.trim(),
          userId: user.uid
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`MCP Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('MCP Server error:', error);
      throw error;
    }
  };

  // Load books from MCP server
  const loadBooks = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await callMCPServer('list_books');
      if (result.status === 'success') {
        setBooks(result.books || []);
      } else {
        throw new Error(result.message || 'Failed to load books');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading books:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Add book via MCP server
  const addBook = async (bookData) => {
    if (!user) return null;
    
    try {
      const result = await callMCPServer('add_book', [bookData.title]);
      if (result.status === 'success') {
        await loadBooks();
        return result.book;
      } else {
        throw new Error(result.message || 'Failed to add book');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error adding book:', err);
      throw err;
    }
  };

  // Update book via MCP server
  const updateBook = async (id, updates) => {
    if (!user) return null;
    
    try {
      const updateString = Object.entries(updates)
        .map(([key, value]) => `${key}:${value}`)
        .join(' ');
      
      const result = await callMCPServer('update_book', [id, updateString]);
      
      if (result.status === 'success') {
        await loadBooks();
        return result.book;
      } else {
        throw new Error(result.message || 'Failed to update book');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error updating book:', err);
      throw err;
    }
  };

  // Delete book via MCP server
  const deleteBook = async (id) => {
    if (!user) return;
    
    try {
      const result = await callMCPServer('delete_book', [id]);
      if (result.status === 'success') {
        await loadBooks();
      } else {
        throw new Error(result.message || 'Failed to delete book');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error deleting book:', err);
      throw err;
    }
  };

  // Load books when user changes
  useEffect(() => {
    if (user) {
      loadBooks();
    } else {
      setBooks([]);
    }
  }, [user, loadBooks]);

  return {
    books,
    loading,
    error,
    addBook,
    updateBook,
    deleteBook,
    loadBooks,
    setError
  };
};
